import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { 
  uploadAxisExcel, 
  getAxisDashboard, 
  updateAxisVoucher, 
  exportAxisMatched,
  refreshAxisMatches,
  clearAxisUnmatched,
  splitAxisVoucherMatch // <-- Added the new API import
} from "../api/misApis";

/* =========================
   DATE FORMATTER
========================= */
const formatDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  return `${dd}-${mm}-${yy}`;
};

function AxisBankAutomation() {
  const navigate = useNavigate();

  /* =========================
     STATE
  ========================= */
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);

  const [matchedRows, setMatchedRows] = useState([]);
  const [unmatchedRows, setUnmatchedRows] = useState([]);

  // SPLIT MODAL STATES
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitTransaction, setSplitTransaction] = useState(null);
  const [splitEntries, setSplitEntries] = useState([{ voucher: "", amount: "" }]);

  /* =========================
     FETCH DATA
  ========================= */
  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const res = await getAxisDashboard();
      setMatchedRows(res.data.matched || []);
      setUnmatchedRows(res.data.unmatched || []);
    } catch (err) {
      console.error("Fetch error:", err);
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Failed to fetch dashboard data!' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  /* =========================
     ACTIONS
  ========================= */
  // ... (handleUpload, handleExport, handleEditVoucher, handleRefresh, handleClearUnmatched remain same) ...

  const handleUpload = async () => {
    if (!file) return Swal.fire({ icon: 'warning', title: 'No file', text: 'Select a file!' });
    try {
      setUploading(true);
      const res = await uploadAxisExcel(file);
      Swal.fire({ icon: 'success', title: 'Uploaded!', text: res.data.message, timer: 2000, showConfirmButton: false });
      setFile(null);
      document.getElementById("axisFileInput").value = ""; 
      fetchDashboard();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err.response?.data?.error || "Upload error" });
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    if (matchedRows.length === 0) return Swal.fire({ icon: 'info', title: 'Empty', text: 'Nothing to export.' });
    try {
      const res = await exportAxisMatched();
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Axis_ERP_Upload_${new Date().getTime()}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      Swal.fire({ icon: 'success', title: 'Exported', text: 'Data moved to archive.' });
      fetchDashboard();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: 'Export failed.' });
    }
  };

  const handleEditVoucher = async (transactionId, currentVoucher) => {
    const { value: newVoucher, isConfirmed } = await Swal.fire({
      title: 'Update Voucher Number',
      input: 'text',
      inputLabel: 'Enter the correct voucher number for this transaction',
      inputValue: currentVoucher || "",
      showCancelButton: true,
      confirmButtonText: 'Update',
      cancelButtonText: 'Cancel',
      buttonsStyling: false, 
      customClass: {
        confirmButton: 'px-5 py-2.5 bg-blue-600 text-white rounded-lg font-semibold shadow hover:bg-blue-700 transition-colors mr-3',
        cancelButton: 'px-5 py-2.5 bg-red-500 text-white rounded-lg font-semibold shadow hover:bg-red-600 transition-colors',
        input: 'border border-gray-300 rounded-lg px-4 py-2 mt-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
      },
      inputValidator: (value) => {
        if (!value || value.trim() === "") {
          return 'You need to write something!';
        }
      }
    });

    if (isConfirmed && newVoucher) {
      try {
        setLoading(true);
        const res = await updateAxisVoucher(transactionId, newVoucher.trim());
        Swal.fire({ 
          icon: res.data.message.includes('matched') ? 'success' : 'info', 
          title: 'Status', 
          text: res.data.message,
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: { confirmButton: 'px-5 py-2.5 bg-blue-600 text-white rounded-lg font-semibold shadow hover:bg-blue-700 transition-colors' }
        });
        fetchDashboard(); 
      } catch (err) {
        Swal.fire({ 
          icon: 'error', 
          title: 'Failed', 
          text: 'Update failed.',
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: { confirmButton: 'px-5 py-2.5 bg-red-600 text-white rounded-lg font-semibold shadow hover:bg-red-700 transition-colors' }
        });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRefresh = async () => {
    try {
      Swal.fire({ title: 'Re-evaluating...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
      const res = await refreshAxisMatches();
      await fetchDashboard();
      Swal.fire({ icon: 'success', title: 'Complete!', text: res.data.message, timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: 'Execution failed.' });
    }
  };

  const handleClearUnmatched = async () => {
    if (unmatchedRows.length === 0) return;
    const { isConfirmed } = await Swal.fire({
      title: 'Purge Queue?', text: "Remove all unmatched transactions?", icon: 'warning', showCancelButton: true, confirmButtonText: 'Purge', buttonsStyling: false,
      customClass: { confirmButton: 'px-5 py-2.5 bg-red-600 text-white rounded-lg mr-3', cancelButton: 'px-5 py-2.5 bg-gray-500 text-white rounded-lg' }
    });

    if (isConfirmed) {
      try {
        setLoading(true);
        await clearAxisUnmatched();
        Swal.fire({ icon: 'success', title: 'Purged!', timer: 1500, showConfirmButton: false });
        fetchDashboard();
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Failed', text: 'Purge failed.' });
      } finally {
        setLoading(false);
      }
    }
  };

  /* =========================
     SPLIT MATCHING LOGIC
  ========================= */
  const openSplitModal = (row) => {
    setSplitTransaction(row);
    setSplitEntries([{ voucher: "", amount: "" }]);
    setShowSplitModal(true);
  };

  const closeSplitModal = () => {
    setShowSplitModal(false);
    setSplitTransaction(null);
    setSplitEntries([{ voucher: "", amount: "" }]);
  };

  const handleSplitChange = (index, field, value) => {
    const newEntries = [...splitEntries];
    newEntries[index][field] = value;
    setSplitEntries(newEntries);
  };

  const addSplitRow = () => setSplitEntries([...splitEntries, { voucher: "", amount: "" }]);
  const removeSplitRow = (index) => setSplitEntries(splitEntries.filter((_, i) => i !== index));

  const totalSplitEntered = splitEntries.reduce((sum, entry) => sum + (parseFloat(entry.amount) || 0), 0);

  const submitSplitMatch = async () => {
    // Shared button style for all validation errors
    const errorSwalConfig = {
      icon: 'error',
      confirmButtonText: 'OK',
      buttonsStyling: false,
      customClass: { confirmButton: 'px-5 py-2.5 bg-red-600 text-white rounded-lg font-semibold shadow hover:bg-red-700 transition-colors' }
    };

    // Validation
    if (totalSplitEntered === 0) return Swal.fire({ ...errorSwalConfig, title: 'Error', text: 'Please enter at least one amount.' });
    if (totalSplitEntered > splitTransaction.Amount) {
      return Swal.fire({ ...errorSwalConfig, title: 'Limit Exceeded', text: `Total entered (₹${totalSplitEntered}) cannot exceed pending bank amount (₹${splitTransaction.Amount}).` });
    }
    
    // Check empty fields
    const hasEmpty = splitEntries.some(e => !e.voucher.trim() || !e.amount);
    if (hasEmpty) return Swal.fire({ ...errorSwalConfig, title: 'Missing Fields', text: 'Please fill all voucher and amount fields before submitting.' });

    try {
      setLoading(true);
      const res = await splitAxisVoucherMatch(splitTransaction.Id, splitEntries);
      closeSplitModal();
      Swal.fire({ 
        icon: 'success', 
        title: 'Matched!', 
        text: res.data.message, 
        timer: 2000, 
        showConfirmButton: false 
      });
      fetchDashboard();
    } catch (err) {
      Swal.fire({
        ...errorSwalConfig,
        title: 'Match Failed',
        text: err.response?.data?.error || "Failed to split match."
      });
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     HELPERS
  ========================= */
  const matchedCols = useMemo(() => (matchedRows[0] ? Object.keys(matchedRows[0]).filter(k => k !== 'AxisTransactionId' && k !== 'Id' && k !== 'OriginalAmount') : []), [matchedRows]);
  const unmatchedCols = useMemo(() => (unmatchedRows[0] ? Object.keys(unmatchedRows[0]).filter(k => k !== 'Id' && k !== 'OriginalAmount') : []), [unmatchedRows]);

  /* =========================
     UI
  ========================= */
  return (
    <div className="p-6 bg-gray-50 min-h-screen font-sans relative">
      {/* ... (TOP BAR & UPLOAD SECTION REMAINS EXACTLY SAME) ... */}
      
      {/* TOP BAR */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 bg-white text-gray-600 rounded-full shadow hover:bg-gray-100 transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Axis Bank Reconciliation</h1>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          <button onClick={handleClearUnmatched} disabled={unmatchedRows.length === 0 || loading} className="px-4 py-2 bg-white text-red-600 border border-red-200 rounded-lg text-sm font-semibold shadow-sm hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50">
            Purge Unmatched Queue
          </button>
          <button onClick={handleRefresh} disabled={loading} className="px-4 py-2 bg-white text-blue-600 border border-blue-200 rounded-lg text-sm font-semibold shadow-sm hover:bg-blue-50 transition-colors flex items-center gap-2 disabled:opacity-50">
            Re-run Auto-Match
          </button>
        </div>
      </div>

      {/* UPLOAD & EXPORT */}
      <div className="bg-white shadow-sm rounded-2xl p-6 mb-8 border border-gray-200 flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex flex-col sm:flex-row items-center gap-4 w-full md:w-auto">
          <input id="axisFileInput" type="file" accept=".xlsx, .xls" onChange={(e) => setFile(e.target.files[0])} className="block w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer border border-gray-200 rounded-lg" />
          <button onClick={handleUpload} disabled={uploading} className={`w-full sm:w-auto px-6 py-2.5 text-white rounded-lg text-sm font-semibold shadow-md flex items-center justify-center gap-2 ${uploading ? "bg-blue-400" : "bg-blue-600 hover:bg-blue-700"}`}>
            Upload Statement
          </button>
        </div>
        <div className="w-full md:w-auto">
          <button onClick={handleExport} disabled={matchedRows.length === 0} className={`w-full md:w-auto px-6 py-2.5 text-white rounded-lg text-sm font-semibold shadow-md flex items-center justify-center gap-2 ${matchedRows.length === 0 ? "bg-gray-300 shadow-none" : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600"}`}>
            Export to Logic/Tally ERP
          </button>
        </div>
      </div>

      {/* MATCHED TABLE */}
      <Table title="Ready to Export (Matched)" columns={matchedCols} rows={matchedRows} loading={loading} headerColor="bg-green-50" titleColor="text-green-800" />

      {/* UNMATCHED TABLE WITH SPLIT BUTTON */}
      <Table
        title="Unmatched / Pending Items"
        columns={[...unmatchedCols, "Action"]} 
        rows={unmatchedRows} 
        loading={loading}
        headerColor="bg-red-50"
        titleColor="text-red-800"
        customRender={(row, col) => {
          if (col === "Action") {
            return (
              <div className="flex items-center gap-2">
                <button onClick={() => handleEditVoucher(row.Id, row.VoucherNumber)} className="px-3 py-1.5 bg-amber-500 text-white rounded-md text-xs font-semibold hover:bg-amber-600 shadow-sm transition-colors whitespace-nowrap">
                  Edit Voucher
                </button>
                <button onClick={() => openSplitModal(row)} title="Split into multiple vouchers" className="flex items-center justify-center w-7 h-7 bg-green-500 text-white rounded-md font-bold hover:bg-green-600 shadow-sm transition-colors text-lg pb-0.5">
                  +
                </button>
              </div>
            );
          }
          if (col === "DebitCredit") return <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${row[col] === 'DR' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{row[col]}</span>;
          if (col === "Amount" || col === "OriginalAmount") return <span className="font-semibold text-gray-700">₹{Number(row[col]).toLocaleString('en-IN')}</span>;
          if (["transactiondate", "date"].some((k) => col.toLowerCase().includes(k))) return formatDate(row[col]);
          return String(row[col] ?? "");
        }}
      />

      {/* SPLIT MODAL UI */}
      {showSplitModal && splitTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-md p-4 sm:p-6">
          {/* Modal Container */}
          <div className="bg-gray-50 rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[95vh] overflow-hidden animate-fade-in-up border border-gray-200">
            
            {/* Modal Header (Sticky) */}
            <div className="px-6 py-5 bg-white border-b border-gray-100 flex justify-between items-center sticky top-0 z-10">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-gray-900 font-bold text-xl tracking-tight">Split Transaction</h2>
                  <p className="text-xs text-gray-500 font-medium mt-0.5">Allocate bank amount to multiple vouchers</p>
                </div>
              </div>
              <button onClick={closeSplitModal} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Modal Body (Scrollable) */}
            <div className="p-6 overflow-y-auto custom-scrollbar flex-1">
              
              {/* Transaction Summary Card */}
              <div className="bg-white border border-gray-200 rounded-xl p-5 mb-6 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Left Side: Details */}
                  <div className="md:col-span-2 space-y-4">
                    <div>
                      <p className="text-[11px] text-gray-500 font-bold tracking-wider uppercase mb-1">Particulars</p>
                      <p className="text-sm text-gray-800 font-semibold leading-relaxed break-words bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                        {splitTransaction.Particulars}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-gray-500 font-bold tracking-wider uppercase mb-1">Transaction Date</p>
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-blue-50 text-blue-700 border border-blue-100">
                        {formatDate(splitTransaction.TransactionDate)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Right Side: Amounts */}
                  <div className="flex flex-col justify-center space-y-4 pl-0 md:pl-6 md:border-l border-gray-100">
                    <div>
                      <p className="text-[11px] text-gray-500 font-bold tracking-wider uppercase mb-1">Original Amount</p>
                      <p className="text-lg text-gray-400 font-bold line-through decoration-gray-300">₹{splitTransaction.OriginalAmount}</p>
                    </div>
                    <div className="bg-amber-50 px-4 py-3 rounded-lg border border-amber-200">
                      <p className="text-[11px] text-amber-700 font-bold tracking-wider uppercase mb-1 flex items-center gap-1.5">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                        Pending Match
                      </p>
                      <p className="text-2xl text-amber-700 font-black">₹{splitTransaction.Amount}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dynamic Form Section */}
              <div>
                <div className="flex justify-between items-end mb-3 px-1">
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide">Voucher Allocation</h3>
                </div>
                
                <div className="space-y-3">
                  {splitEntries.map((entry, index) => (
                    <div key={index} className="group flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 bg-white border border-gray-200 p-4 rounded-xl shadow-sm focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-400 transition-all">
                      <div className="hidden sm:flex w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 items-center justify-center text-xs font-black shrink-0">
                        {index + 1}
                      </div>
                      
                      <div className="w-full sm:flex-1">
                        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Voucher No.</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                          </div>
                          <input type="text" value={entry.voucher} onChange={(e) => handleSplitChange(index, 'voucher', e.target.value)} placeholder="Enter Voucher" className="w-full pl-9 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-semibold text-gray-800 focus:bg-white focus:outline-none transition-colors uppercase placeholder:normal-case placeholder:font-normal" />
                        </div>
                      </div>

                      <div className="w-full sm:flex-1">
                        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">Amount (₹)</label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 font-semibold">₹</span>
                          </div>
                          <input type="number" min="1" value={entry.amount} onChange={(e) => handleSplitChange(index, 'amount', e.target.value)} placeholder="0.00" className="w-full pl-8 pr-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm font-bold text-gray-800 focus:bg-white focus:outline-none transition-colors placeholder:font-normal" />
                        </div>
                      </div>

                      <div className="sm:pt-6 shrink-0 absolute sm:relative right-4 top-4 sm:right-0 sm:top-0 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                        {splitEntries.length > 1 && (
                          <button onClick={() => removeSplitRow(index)} className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Remove Entry">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer (Sticky) */}
            <div className="bg-white border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row justify-between items-center gap-4 sticky bottom-0 z-10 rounded-b-2xl">
              
              <div className="flex items-center gap-4 w-full sm:w-auto">
                <button onClick={addSplitRow} className="text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-4 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center gap-2 transition-colors w-full sm:w-auto border border-indigo-100">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>
                  Add Row
                </button>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 w-full sm:w-auto justify-between sm:justify-end">
                {/* Live Total Calculation */}
                <div className="flex flex-col text-right">
                  <span className="text-[11px] text-gray-500 font-bold uppercase tracking-wider mb-0.5">Total Allocated</span>
                  <div className="flex items-baseline gap-1 justify-end">
                    <span className={`text-xl font-black ${totalSplitEntered > splitTransaction.Amount ? 'text-red-600' : totalSplitEntered === splitTransaction.Amount ? 'text-emerald-600' : 'text-blue-600'}`}>
                      ₹{totalSplitEntered}
                    </span>
                    <span className="text-sm font-bold text-gray-400">/ ₹{splitTransaction.Amount}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 w-full sm:w-auto mt-2 sm:mt-0">
                  <button onClick={closeSplitModal} className="flex-1 sm:flex-none px-5 py-2.5 text-sm font-bold text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-800 transition-colors">
                    Cancel
                  </button>
                  <button onClick={submitSplitMatch} disabled={loading} className="flex-1 sm:flex-none px-6 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-md shadow-indigo-200 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                    {loading ? (
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    )}
                    Submit Match
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ... (Table and colRenderer remain exactly same) ...

const Table = ({ title, columns, rows, loading, customRender, headerColor = "bg-gray-100", titleColor = "text-gray-800" }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  useEffect(() => { setPage(1); }, [searchTerm, rows]);

  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const lower = searchTerm.toLowerCase();
    return rows.filter((row) => Object.values(row).some((val) => String(val ?? "").toLowerCase().includes(lower)));
  }, [rows, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const paginatedRows = filteredRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <div className="mb-8 bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className={`flex flex-col sm:flex-row justify-between items-center px-6 py-4 border-b border-gray-200 gap-4 ${headerColor}`}>
        <h2 className={`text-lg font-bold ${titleColor} flex items-center gap-2`}>
          {title} <span className="bg-white px-2 py-0.5 rounded-full text-sm shadow-sm border border-gray-200 text-gray-600">{filteredRows.length}</span>
        </h2>
        <div className="relative w-full sm:w-72">
          <svg className="w-5 h-5 text-gray-400 absolute left-3 top-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input type="text" placeholder="Search records..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
      </div>
      <div className="overflow-x-auto">
        {loading ? (
          <div className="p-10 flex justify-center items-center gap-3 text-gray-500">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            <span className="font-medium">Loading Data...</span>
          </div>
        ) : (
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-500 uppercase text-xs font-semibold tracking-wider">
              <tr>{columns.map((c) => (<th key={c} className="border-b px-6 py-3 whitespace-nowrap">{c.replace(/([A-Z])/g, ' $1').trim()}</th>))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedRows.length === 0 ? (
                <tr><td colSpan={columns.length} className="text-center p-8 text-gray-400 font-medium">No data available</td></tr>
              ) : (
                paginatedRows.map((r, i) => (
                  <tr key={i} className="hover:bg-blue-50/50 transition-colors">
                    {columns.map((c) => (<td key={c} className="px-6 py-3 text-gray-600">{customRender ? customRender(r, c) : colRenderer(r, c)}</td>))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      {filteredRows.length > 0 && (
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center text-sm text-gray-600">
          <span className="font-medium">Page <span className="text-gray-900">{page}</span> of <span className="text-gray-900">{totalPages}</span></span>
          <div className="flex gap-2">
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)} className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-white bg-white">Previous</button>
            <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)} className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-white bg-white">Next</button>
          </div>
        </div>
      )}
    </div>
  );
};

const colRenderer = (row, col) => {
  if (col === "DebitCredit") return <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${row[col] === 'DR' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{row[col]}</span>;
  if (col === "Amount" || col === "OriginalAmount") return <span className="font-semibold text-gray-700">₹{Number(row[col]).toLocaleString('en-IN')}</span>;
  if (["transactiondate", "date"].some((k) => col.toLowerCase().includes(k))) return formatDate(row[col]);
  return String(row[col] ?? "");
};

export default AxisBankAutomation;